import { VersionedIntegrations } from '@dadohr/shared/domainTypes/globalConfig';
import { styled, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { Loader } from '../../../../components/Loader';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import { useStringQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { IntegrationSetupCompleteModal } from '../../../Org/pages/Integrations/integrations/common';

export const PageOAuthRedirectContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: '100%',

  textAlign: 'center',
  gap: p.theme.spacing()
}));

export type PageOAuthRedirectProps = {
  integrationName: string;
  cloudFn: string;
  codeParam?: string;

  // TODO: FOR MIGRATION
  internalIntegrationName?: VersionedIntegrations;
};

const OauthErrorReason = ({
  integrationName,
  errorParam = 'error',
  errorSubcodeParam = 'error_subcode',
  errorDescriptionParam = 'error_description'
}: {
  integrationName: string;
  errorParam?: string;
  errorSubcodeParam?: string;
  errorDescriptionParam?: string;
}) => {
  const { t } = useUnsafeTranslation();

  const [error] = useStringQueryParam(errorParam);
  const [errorSubcode] = useStringQueryParam(errorSubcodeParam);
  const [errorDescription] = useStringQueryParam(errorDescriptionParam);

  if (integrationName === 'Microsoft') {
    if (error === 'access_denied' && errorSubcode === 'cancel')
      return (
        <>
          <p>
            {t(
              'Dado has not yet been granted consent to access your Microsoft 365.'
            )}
          </p>
          <p>
            {t(
              'Please try again or, if you have requested consent, please wait for the request to granted by the relevant person in your organization.'
            )}
          </p>
          <div>{errorDescription}</div>
        </>
      );
  }

  return <div>{t('Something went wrong')}.</div>;
};

export const PageOAuthRedirect = ({
  integrationName,
  internalIntegrationName,
  cloudFn,
  codeParam = 'code'
}: PageOAuthRedirectProps) => {
  const { org } = useCurrentUser();
  const [code] = useStringQueryParam(codeParam);
  const [stateParam] = useStringQueryParam('state');
  const { t } = useTranslation();
  const [state, setState] = useState<'pending' | 'success' | 'error'>(
    'pending'
  );

  useEffect(() => {
    if (state === 'pending') {

      callFirebaseFunction(cloudFn, {
        orgId: org.id,
        code,
        state: stateParam
      }, 550000, internalIntegrationName).then(
        () => {
          setState('success');
        },
        () => setState('error')
      );
    }
  }, [state, stateParam, code, org.id, cloudFn, internalIntegrationName]);

  const {
    dialogOpen: successDialogOpen,
    closeDialog: closeSuccessDialog
  } = useDialogState(true);
  return (
    <PageOAuthRedirectContainer>
      {state === 'pending' && (
        <div>
          <Loader />
          <br />
          <div>{t('oauth.finishingSetup', { name: integrationName })}</div>
        </div>
      )}
      {state === 'success' && (
        <div>
          <Typography component="h3" variant="h3">
            {t('oauth.setupComplete', { name: integrationName })}.
          </Typography>
          <br />
          <div>{t('oauth.setupCompleteNextSteps')}</div>
          {/* Right now Google Workspace is the only OAuth integration that works as an import source */}
          {integrationName === 'Google' && (
            <IntegrationSetupCompleteModal
              integrationName={integrationName}
              open={successDialogOpen}
              onClose={closeSuccessDialog}
            />
          )}
        </div>
      )}

      {state === 'error' && (
        <OauthErrorReason integrationName={integrationName} />
      )}
    </PageOAuthRedirectContainer>
  );
};

export const PageOAuthRedirectV2 = ({
  integrationName
}: {
  integrationName: string;
}) => {
  const { t } = useTranslation();

  const [error] = useStringQueryParam('error');
  const {
    dialogOpen: successDialogOpen,
    closeDialog: closeSuccessDialog
  } = useDialogState(true);
  return (
    <PageOAuthRedirectContainer>
      {!error && (
        <div>
          <Typography component="h3" variant="h3">
            {t('oauth.setupComplete', { name: integrationName })}.
          </Typography>
          <br />
          <div>{t('oauth.setupCompleteNextSteps')}</div>
          {/* Right now Google Workspace is the only OAuth integration that works as an import source */}
          {integrationName === 'Google' ||
            (integrationName === 'Airtable' && (
              <IntegrationSetupCompleteModal
                integrationName={integrationName}
                open={successDialogOpen}
                onClose={closeSuccessDialog}
              />
            ))}
        </div>
      )}
      {error && (
        <div>
          <Typography component="h3" variant="h3">
            {t('oauth.setupFailed', { name: integrationName })}.
          </Typography>
          <br />
          <div>{t('oauth.setupFailedNextSteps')}</div>
        </div>
      )}
    </PageOAuthRedirectContainer>
  );
};
